/*
 * @Description: 实时定位驾驶舱 — 底部时间轴控件
 *   左：轨迹时长下拉；中左：实时/回到实时/历史 按钮；中：时间轴滑块（hover 气泡显示时刻）。
 *   拖动滑块 = 选取历史时刻做全员快照回放；拉到最右 = 回到实时。
 */
import React, { useRef, useState } from 'react'
import { Select, Slider } from 'antd'
import dayjs from 'dayjs'
import { CockpitMapStyle, TRACK_DURATION_OPTIONS } from '../../constants'
import MapStyleSelect from '../MapStyleSelect'
import './index.scss'

const { Option } = Select

interface BottomTimelineProps {
  /** 右侧栏两张卡是否均收起（收起后时间轴向右延伸占满） */
  sidebarCollapsed?: boolean
  /** 当前轨迹时长（毫秒） */
  duration: number
  /** 时长选择器是否禁用（历史态锁定 24h） */
  durationDisabled?: boolean
  /** 时长变化 */
  onChangeDuration: (duration: number) => void
  /** 地图样式 */
  mapStyle: CockpitMapStyle
  /** 地图样式变化 */
  onChangeMapStyle: (style: CockpitMapStyle) => void
  /** 时间轴起点（unix 毫秒） */
  rangeStart: number
  /** 时间轴终点（unix 毫秒，实时态=当前时刻） */
  rangeEnd: number
  /** 当前指示时刻（unix 毫秒） */
  value: number
  /** 是否处于实时态视图（指示器在最右、未拖到历史） */
  isRealtime: boolean
  /** 是否历史态（date≠当天，时间轴只读回放，按钮显示「历史」） */
  isHistorical: boolean
  /** 拖动时间轴（传入选中的时刻 ms） */
  onScrub: (timeMs: number) => void
  /** 点击「回到实时」 */
  onBackToRealtime: () => void
}

const BottomTimeline: React.FC<BottomTimelineProps> = ({
  sidebarCollapsed,
  duration,
  durationDisabled,
  onChangeDuration,
  mapStyle,
  onChangeMapStyle,
  rangeStart,
  rangeEnd,
  value,
  isRealtime,
  isHistorical,
  onScrub,
  onBackToRealtime
}) => {
  const formatTime = (ms: number) => dayjs(ms).format('HH:mm')

  // 鼠标在时间轴上 hover 时，显示该位置对应时刻的浮动气泡
  const trackRef = useRef<HTMLDivElement>(null)
  const [hoverTip, setHoverTip] = useState<{ x: number; time: number } | null>(
    null
  )
  const handleTrackMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current
    if (!el || rangeEnd <= rangeStart) return
    const rect = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    setHoverTip({
      x: ratio * rect.width,
      time: rangeStart + ratio * (rangeEnd - rangeStart)
    })
  }
  const handleTrackLeave = () => setHoverTip(null)

  return (
    <div
      className={`cockpit-timeline ${
        sidebarCollapsed ? 'cockpit-timeline--wide' : ''
      }`}
    >
      {/* 最左：地图样式选择（独立浮条卡片） */}
      <div className="cockpit-timeline__box cockpit-timeline__mapstyle-box">
        <MapStyleSelect value={mapStyle} onChange={onChangeMapStyle} />
      </div>

      {/* 左侧独立浮条：轨迹时长 */}
      <div className="cockpit-timeline__box cockpit-timeline__duration-box">
        <Select
          size="small"
          value={duration}
          disabled={durationDisabled}
          className="cockpit-timeline__duration"
          onChange={onChangeDuration}
        >
          {TRACK_DURATION_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>

      {/* 右侧独立浮条：实时按钮 + 时间轴 */}
      <div className="cockpit-timeline__box cockpit-timeline__track-box">
        {isHistorical ? (
          <span className="cockpit-timeline__state is-historical">历史</span>
        ) : (
          <button
            type="button"
            className={`cockpit-timeline__state ${
              isRealtime ? 'is-realtime' : 'is-back'
            }`}
            onClick={onBackToRealtime}
          >
            {isRealtime ? '● 实时' : '回到实时'}
          </button>
        )}

        <span className="cockpit-timeline__label">
          {formatTime(rangeStart)}
        </span>
        <div
          className="cockpit-timeline__track"
          ref={trackRef}
          onMouseMove={handleTrackMove}
          onMouseLeave={handleTrackLeave}
        >
          {hoverTip && (
            <span
              className="cockpit-timeline__hovertip"
              style={{ left: hoverTip.x }}
            >
              {formatTime(hoverTip.time)}
            </span>
          )}
          <Slider
            className="cockpit-timeline__slider"
            min={rangeStart}
            max={rangeEnd}
            value={value}
            step={60 * 1000}
            tooltip={{ formatter: (v) => formatTime(v as number) }}
            onChange={(v) => onScrub(v as number)}
          />
        </div>
        <span className="cockpit-timeline__label">{formatTime(rangeEnd)}</span>
      </div>
    </div>
  )
}

export default BottomTimeline
